import { GetAllUsers } from "../FirebaseQueries";
import React, { useEffect } from "react";
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowId } from "@mui/x-data-grid";
import { Alert, Box, Button, Collapse, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Stack } from "@mui/material";
import { Timestamp } from "firebase/firestore";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../AuthProvider";

interface User {
    id: string;
    email: string;
    fullName: string;
    role: string;
    lastActive: Timestamp;
}

interface Role {
    value: string;
    name: string;
}

export default function Users() {
    const allRolesKey: string = "allroles";

    const [users, setUsers] = React.useState<User[]>([]);
    const [roleFilter, setRoleFilter] = React.useState<string>(allRolesKey);
    const [passwordResetSent, setPasswordResetSent] = React.useState<boolean>(false);

    const { resetPassword } = useAuth();

    const columns: GridColDef[] = [
        {
            field: "fullName",
            headerName: "Name",
            flex: 1,
            renderCell: (params: GridRenderCellParams<any, string>) => (
                <a href={`/app/users/${params.id}`}>
                    {params.value}
                </a>
            ),
        },
        { field: "email", headerName: "E-mail", flex: 1 },
        { field: "role", headerName: "Role", flex: 1, valueFormatter: (value: string) => Capitalize(value) },
        { field: "lastActive", headerName: "Last Active", flex: 1, valueFormatter: (value: Timestamp) => FormatTimestamp(value) },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [<GridActionsCellItem icon={<EmailIcon />} label="Password Reset" className="textPrimary" onClick={HandleResetPasswordClick(id)} color="inherit" />];
            },
        },
    ];

    const roles: Role[] = [
        { value: allRolesKey, name: "All Roles" },
        { value: "teacher", name: "Teacher" },
        { value: "student", name: "Student" },
    ];

    const HandleResetPasswordClick = (id: GridRowId) => () => {
        const clickedRow = users.find((user) => user.id === id);
        if (clickedRow !== undefined) {
            // TODO - this doesn't check if it has been sent, just that the email exists
            resetPassword(clickedRow.email);
            setPasswordResetSent(true);
        }
    };

    function HandleFilterChange(event: SelectChangeEvent) {
        setRoleFilter(event.target.value);
    }

    function Capitalize(value: string): string {
        if (value === "" || value === undefined || value === null) return "";
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    function FormatTimestamp(ts: Timestamp): string {
        if (ts === undefined) return "N/A";

        let date: Date = ts.toDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }

    function FormatTimestampCSV(ts: Timestamp): string {
        if (ts === undefined) return "";
        return ts.toDate().toString();
    }

    function DownloadCSVFile() {
        let headers: string = "";
        columns.forEach((col: GridColDef) => {
            headers += `${col.headerName},`;
        });
        headers += "\n";

        let rows: string = "";
        FilteredUsers().forEach((user: User) => {
            rows += `${user.fullName},${user.email},${user.role},${FormatTimestampCSV(user.lastActive)},\n`;
        });

        let csvContent = "data:text/csv;charset=utf-8," + headers + rows;

        var encodedUri = encodeURI(csvContent);
        window.open(encodedUri);
    }

    function FilteredUsers(): User[] {
        if (roleFilter === "" || roleFilter === allRolesKey) return users;

        return users.filter((user: User) => user.role === roleFilter);
    }

    useEffect(() => {
        const getAllUsers = async () => {
            try {
                const studentsSnapshot = await GetAllUsers();
                const value: User[] = [];
                studentsSnapshot.forEach((doc) => {
                    value.push({
                        id: doc.id,
                        email: doc.data().email,
                        fullName: doc.data().fullName,
                        role: doc.data().role,
                        lastActive: doc.data().lastActive,
                    });
                });

                value.sort((a, b) => {
                    if (a.email < b.email) {
                        return -1;
                    }
                    if (a.email > b.email) {
                        return 1;
                    }
                    return 0;
                });
                setUsers(value);
            } catch (err) {
                // TODO
                console.log(err);
            }
        };
        getAllUsers();
    }, []);

    return (
        <Stack spacing={2}>
            <Collapse in={passwordResetSent} mountOnEnter unmountOnExit>
                <Alert
                    icon={<EmailIcon fontSize="inherit" />}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setPasswordResetSent(false);
                            }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    Password reset email sent!
                </Alert>
            </Collapse>
            <Box sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
                <Select value={roleFilter} onChange={HandleFilterChange}>
                    {roles.map((role: Role, index: number) => {
                        return (
                            <MenuItem value={role.value} key={index}>
                                {role.name}
                            </MenuItem>
                        );
                    })}
                </Select>
                <Button onClick={DownloadCSVFile} variant="contained">
                    Export CSV
                </Button>
            </Box>
            <Paper variant="outlined">
                <DataGrid
                    pageSizeOptions={[15, 50]}
                    sx={{ p: 1 }}
                    rows={FilteredUsers()}
                    columns={columns}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 15 } },
                    }}
                />
            </Paper>
        </Stack>
    );
}
